/*
  drs-embroidery-bridge — makes the embroidery add-on charge correctly on the
  STANDARD product page (sections/main-product.liquid).

  THE PROBLEM
  -----------
  Shopify cannot surcharge a line item from the storefront, so embroidery is
  priced as a SECOND real product added as an extra cart line (Text SAR 44,
  Icon SAR 34, Logo digitisation SAR 88). sections/figs-product.liquid does that
  with its own hand-written /cart/add.js call. main-product uses Symmetry's
  ProductForm instead (assets/main.js:2811), which posts exactly one item — so a
  straight port would show the embroidery UI, take the personalisation, and never
  charge for it.

  THE FIX, AND WHY THIS SHAPE
  ---------------------------
  Shopify's cart endpoint accepts a multi-item form body:
      items[0][id], items[0][quantity], items[0][properties][Name]
      items[1][id], items[1][quantity]
  You cannot mix that with a plain `id=` field, so the form has to be rewritten
  into items[] form. This listener does that in the CAPTURE phase, immediately
  before ProductForm reads the form — so the garment and the paid add-on travel
  in the theme's own single request.

  One request matters. The obvious alternative — let the theme add the garment,
  then add the add-on afterwards — can half-succeed, leaving a personalised
  garment in the cart that was never paid for. Atomicity is the whole point.

  It only rewrites when embroidery is actually confirmed on this page. Every
  other product's buy form is untouched.

  LOGO UPLOADS take a file, and a file cannot travel in the JSON cart route, so
  those fall back to a native (multipart) form post, the same escape hatch
  figs-product.liquid uses.
*/
(() => {
  const emb = () => document.querySelector('figs-embroidery, drs-hh-embroidery');

  const addons = () => {
    const el = emb();
    if (!el || typeof el.getAddonItems !== 'function') return [];
    try {
      return el.getAddonItems() || [];
    } catch (e) {
      return [];
    }
  };

  /** Rewrite `id` / `quantity` / `properties[X]` into items[0][...] form. */
  const toItemsForm = (form, extra) => {
    const rename = (input, name) => { input.name = name; };

    // form.elements, NOT form.querySelectorAll: assets/figs-embroidery.js portals the
    // modal to <body> to escape a stacking context, so the logo upload is associated
    // with this form by the HTML `form` attribute rather than by being inside it.
    // A descendant query silently misses it, which would leave one stray
    // properties[Logo file] field beside the renamed items[0][...] ones and produce a
    // cart request that is neither shape. form.elements includes both.
    const fields = Array.prototype.slice.call(form.elements);
    const byName = (n) => fields.filter((el) => el.name === n)[0];

    const idInput = byName('id');
    if (!idInput) return false;
    rename(idInput, 'items[0][id]');

    const qty = byName('quantity');
    if (qty) rename(qty, 'items[0][quantity]');

    fields.filter((el) => el.name.indexOf('properties[') === 0).forEach((el) => {
      // properties[Embroidery] -> items[0][properties][Embroidery]
      const key = el.name.slice('properties['.length, -1);
      rename(el, `items[0][properties][${key}]`);
    });

    extra.forEach((item, i) => {
      const n = i + 1;
      const add = (suffix, value) => {
        const h = document.createElement('input');
        h.type = 'hidden';
        h.name = `items[${n}]${suffix}`;
        h.value = value;
        h.dataset.drsEmbAddon = 'true';
        form.appendChild(h);
      };
      add('[id]', item.id);
      add('[quantity]', item.quantity || 1);
      if (item.properties) {
        Object.entries(item.properties).forEach(([k, v]) => add(`[properties][${k}]`, v));
      }
    });
    return true;
  };

  document.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (!form.classList || !form.classList.contains('js-product-form')) return;
    if (!document.querySelector('.section-main-product')) return;

    const el = emb();
    if (!el) return;

    // Only the form that actually CONTAINS the embroidery panel may add the paid
    // line. A main-product page carries a second .js-product-form in the sticky
    // add-to-cart bar with none of the properties[Embroidery…] inputs; charging
    // from there billed the add-on against a garment line with no
    // personalisation, and double-charged when the main form had already been used.
    if (!form.contains(el)) return;

    const extra = addons();
    if (!extra.length) return; // nothing confirmed — leave the form alone

    // A logo upload carries a file; the cart's JSON route cannot. Stop the
    // theme's AJAX handler and let the browser post the form natively.
    if (typeof el.needsNativeSubmit === 'function' && el.needsNativeSubmit()) {
      evt.stopImmediatePropagation();
      toItemsForm(form, extra);
      form.removeAttribute('data-ajax');
      form.submit();
      return;
    }

    toItemsForm(form, extra);
    // Not prevented: ProductForm's own submit handler runs next and posts the
    // rewritten body, so the garment and add-on land in one request.
  }, true); // capture — must run before ProductForm's listener
})();
